<script setup>
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  form: { type: Object, required: true },
  isActive: { type: Boolean, default: false },
  conversationAttributes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update', 'remove', 'set-active']);

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'list', label: 'List' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'link', label: 'Link' },
];

const isExpanded = ref(true);
const formName = ref(props.form.name);
const fields = ref([...(props.form.fields || [])]);

const existingAttributeOptions = computed(() =>
  props.conversationAttributes
    .filter(attr => !fields.value.some(f => f.key === attr.attribute_key))
    .map(attr => ({
      key: attr.attribute_key,
      label: attr.attribute_display_name,
      display_type: attr.attribute_display_type,
      description: attr.attribute_description || '',
      attribute_values: attr.attribute_values || [],
      regex_pattern: attr.regex_pattern || '',
      regex_cue: attr.regex_cue || '',
    }))
);

function generateKey(label) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function emitUpdate() {
  emit('update', {
    ...props.form,
    name: formName.value,
    fields: fields.value,
  });
}

function addField() {
  fields.value.push({
    key: '',
    label: '',
    display_type: 'text',
    description: '',
    regex_pattern: '',
    regex_cue: '',
    attribute_values: [],
  });
  emitUpdate();
}

function addExistingAttribute(attr) {
  fields.value.push({ ...attr });
  emitUpdate();
}

function removeField(index) {
  fields.value.splice(index, 1);
  emitUpdate();
}

function onFieldLabelChange(index) {
  const field = fields.value[index];
  if (!field.key || field.key === generateKey(field.prevLabel || '')) {
    field.key = generateKey(field.label);
  }
  field.prevLabel = field.label;
  emitUpdate();
}
</script>

<template>
  <div class="border border-n-strong rounded-lg overflow-hidden">
    <!-- Form Header -->
    <div
      class="flex items-center justify-between px-4 py-3 bg-n-alpha-1 cursor-pointer"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-chevron-right text-n-slate-11 transition-transform"
          :class="{ 'rotate-90': isExpanded }"
        />
        <input
          v-model="formName"
          type="text"
          class="!h-7 !mb-0 !w-48 text-sm font-medium"
          :placeholder="$t('INBOX_MGMT.AI_PREFILL.FORM_NAME_PLACEHOLDER')"
          @input="emitUpdate"
          @click.stop
        />
        <span
          v-if="isActive"
          class="text-xs px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11 font-medium"
        >
          {{ $t('INBOX_MGMT.AI_PREFILL.ACTIVE') }}
        </span>
      </div>
      <div class="flex items-center gap-1" @click.stop>
        <NextButton v-if="!isActive" xs faded @click="emit('set-active')">
          {{ $t('INBOX_MGMT.AI_PREFILL.SET_ACTIVE') }}
        </NextButton>
        <NextButton
          xs
          slate
          ghost
          icon="i-lucide-trash-2"
          @click="emit('remove')"
        />
      </div>
    </div>

    <!-- Fields -->
    <div v-if="isExpanded" class="p-4 space-y-3">
      <div
        v-for="(field, index) in fields"
        :key="index"
        class="flex items-start gap-2 p-3 bg-n-alpha-1 rounded-lg"
      >
        <div class="flex-1 grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_LABEL') }}
            </label>
            <input
              v-model="field.label"
              type="text"
              class="!h-7 !mb-0 text-sm"
              :placeholder="$t('INBOX_MGMT.AI_PREFILL.FIELD_LABEL_PLACEHOLDER')"
              @input="onFieldLabelChange(index)"
            />
          </div>
          <div>
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_KEY') }}
            </label>
            <input
              v-model="field.key"
              type="text"
              class="!h-7 !mb-0 text-sm font-mono"
              :placeholder="$t('INBOX_MGMT.AI_PREFILL.FIELD_KEY_PLACEHOLDER')"
              @input="emitUpdate"
            />
          </div>
          <div>
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_TYPE') }}
            </label>
            <select
              v-model="field.display_type"
              class="!h-7 !mb-0 text-sm"
              @change="emitUpdate"
            >
              <option
                v-for="type in FIELD_TYPES"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_DESCRIPTION') }}
            </label>
            <input
              v-model="field.description"
              type="text"
              class="!h-7 !mb-0 text-sm"
              :placeholder="
                $t('INBOX_MGMT.AI_PREFILL.FIELD_DESCRIPTION_PLACEHOLDER')
              "
              @input="emitUpdate"
            />
          </div>
          <div v-if="field.display_type === 'text'" class="col-span-2">
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_REGEX') }}
            </label>
            <input
              v-model="field.regex_pattern"
              type="text"
              class="!h-7 !mb-0 text-sm font-mono"
              :placeholder="$t('INBOX_MGMT.AI_PREFILL.FIELD_REGEX')"
              @input="emitUpdate"
            />
          </div>
          <div v-if="field.display_type === 'list'" class="col-span-2">
            <label class="block text-xs text-n-slate-11 mb-0.5">
              {{ $t('INBOX_MGMT.AI_PREFILL.FIELD_OPTIONS') }}
            </label>
            <input
              :value="(field.attribute_values || []).join(', ')"
              type="text"
              class="!h-7 !mb-0 text-sm"
              :placeholder="
                $t('INBOX_MGMT.AI_PREFILL.FIELD_OPTIONS_PLACEHOLDER')
              "
              @input="
                e => {
                  field.attribute_values = e.target.value
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean);
                  emitUpdate();
                }
              "
            />
          </div>
        </div>
        <NextButton
          xs
          slate
          ghost
          icon="i-lucide-x"
          class="mt-4"
          @click="removeField(index)"
        />
      </div>

      <!-- Add Field Actions -->
      <div class="flex items-center gap-2">
        <NextButton xs faded icon="i-lucide-plus" @click="addField">
          {{ $t('INBOX_MGMT.AI_PREFILL.ADD_FIELD') }}
        </NextButton>
        <div v-if="existingAttributeOptions.length > 0" class="relative group">
          <NextButton xs faded icon="i-lucide-link">
            {{ $t('INBOX_MGMT.AI_PREFILL.ADD_EXISTING') }}
          </NextButton>
          <div
            class="hidden group-hover:block absolute z-10 top-full left-0 mt-1 bg-n-solid-1 border border-n-strong rounded-lg shadow-lg p-1 min-w-48 max-h-48 overflow-y-auto"
          >
            <button
              v-for="attr in existingAttributeOptions"
              :key="attr.key"
              class="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-n-alpha-2"
              @click="addExistingAttribute(attr)"
            >
              {{ `${attr.label} (${attr.key})` }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
